"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckIcon, CopyIcon } from "lucide-react";
import { Highlight, themes } from "prism-react-renderer";
import React, { useState } from "react";
import { useI18n } from "@llmgateway/i18n/react";
import type { Language } from "prism-react-renderer";

// Custom dark theme inspired by fumadocs/shiki
const customDarkTheme = {
  ...themes.vsDark,
  plain: {
    color: "#d4d4d4",
    backgroundColor: "#1e1e1e"
  },
  styles: [...themes.vsDark.styles, {
    types: ["comment", "prolog", "doctype", "cdata"],
    style: {
      color: "#6A9955",
      fontStyle: "italic" as const
    }
  }, {
    types: ["namespace"],
    style: {
      opacity: 0.7
    }
  }, {
    types: ["string", "attr-value"],
    style: {
      color: "#CE9178"
    }
  }, {
    types: ["punctuation", "operator"],
    style: {
      color: "#d4d4d4"
    }
  }, {
    types: ["entity", "url", "symbol", "number", "boolean", "constant"],
    style: {
      color: "#B5CEA8"
    }
  }, {
    types: ["tag", "selector", "attr-name"],
    style: {
      color: "#569CD6"
    }
  }, {
    types: ["function", "deleted"],
    style: {
      color: "#DCDCAA"
    }
  }, {
    types: ["keyword", "property"],
    style: {
      color: "#569CD6"
    }
  }, {
    types: ["class-name"],
    style: {
      color: "#4EC9B0"
    }
  }, {
    types: ["variable"],
    style: {
      color: "#9CDCFE"
    }
  }]
};

// Custom light theme
const customLightTheme = {
  ...themes.github,
  plain: {
    color: "#24292e",
    backgroundColor: "#f6f8fa"
  }
};
function normalizeLanguageName(rawLanguageName: string | undefined): Language {
  if (!rawLanguageName) {
    return "text";
  }
  const value = rawLanguageName.toLowerCase();
  const aliasToLanguage: Record<string, Language> = {
    js: "javascript",
    javascript: "javascript",
    mjs: "javascript",
    cjs: "javascript",
    ts: "typescript",
    tsx: "tsx",
    typescript: "typescript",
    jsx: "jsx",
    sh: "bash",
    shell: "bash",
    bash: "bash",
    zsh: "bash",
    yml: "yaml",
    yaml: "yaml",
    json: "json",
    json5: "json",
    html: "markup",
    markup: "markup",
    css: "css",
    scss: "scss",
    md: "markdown",
    markdown: "markdown",
    python: "python",
    py: "python",
    go: "go",
    golang: "go",
    java: "java",
    c: "c",
    cpp: "cpp",
    rust: "rust",
    sql: "sql",
    docker: "docker",
    dockerfile: "docker",
    diff: "diff"
  };
  return aliasToLanguage[value] ?? "text";
}

// Extract filename from className like "language-typescript title='ai-sdk.ts'" or "language-typescript:ai-sdk.ts"
function extractFileInfo(className: string | undefined): {
  language: Language;
  filename?: string;
} {
  if (!className) {
    return {
      language: "text"
    };
  }
  let language: Language = "text";
  let filename: string | undefined;
  const parts = className.split(/\s+/g);
  for (const part of parts) {
    // Check for language-xxx or lang-xxx
    const langMatch = part.match(/^(?:language|lang)-([A-Za-z0-9_-]+)/);
    if (langMatch) {
      const langPart = langMatch[1];
      // Check if it includes a filename like "typescript:ai-sdk.ts"
      const colonIndex = langPart.indexOf(":");
      if (colonIndex > 0) {
        language = normalizeLanguageName(langPart.substring(0, colonIndex));
        filename = langPart.substring(colonIndex + 1);
      } else {
        language = normalizeLanguageName(langPart);
      }
      continue;
    }

    // Check for title='filename' or title="filename"
    const titleMatch = part.match(/^title=['"](.+?)['"]$/);
    if (titleMatch) {
      filename = titleMatch[1];
    }
  }
  return {
    language,
    filename
  };
}

// Copy button component
function CopyButton(t0) {
  const $ = _c(10);
  const {
    code
  } = t0;
  const [copied, setCopied] = useState(false);
  const {
    t
  } = useI18n();
  let t1;
  if ($[0] !== code) {
    t1 = async () => {
      if (!navigator.clipboard?.writeText) {
        return;
      }
      try {
        await navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        setCopied(false);
      }
    };
    $[0] = code;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleCopy = t1;
  let t2;
  if ($[2] !== t) {
    t2 = t("common.copyCode");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== copied) {
    t3 = copied ? <CheckIcon className="h-4 w-4 text-green-500" /> : <CopyIcon className="h-4 w-4 text-muted-foreground" />;
    $[4] = copied;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== handleCopy || $[7] !== t2 || $[8] !== t3) {
    t4 = <button type="button" onClick={handleCopy} className="absolute top-3 right-3 p-2 rounded-md bg-muted/50 hover:bg-muted transition-colors z-10" aria-label={t2}>{t3}</button>;
    $[6] = handleCopy;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

// Determine diff line color based on leading +/- character
function getDiffColor(language: Language, line: {
  content: string;
}[], mode: "light" | "dark"): string | undefined {
  if (language !== "diff") {
    return undefined;
  }
  const lineText = line.map(t => t.content).join("");
  if (lineText.startsWith("+")) {
    return mode === "light" ? "#16a34a" : "#4ade80";
  }
  if (lineText.startsWith("-")) {
    return mode === "light" ? "#dc2626" : "#f87171";
  }
  return undefined;
}

// Syntax highlighted pre component
export const SyntaxHighlightedPre = t0 => {
  const $ = _c(38);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  let code = "";
  let filename;
  let language;
  if ($[3] !== children) {
    language = "text";
    if (children && typeof children === "object" && "props" in children) {
      const childProps = (children as {
        props?: {
          children?: string;
          className?: string;
        };
      }).props;
      if (childProps?.children) {
        let t1;
        if ($[7] !== childProps.children) {
          t1 = childProps.children.trim();
          $[7] = childProps.children;
          $[8] = t1;
        } else {
          t1 = $[8];
        }
        code = t1;
      }
      if (childProps?.className) {
        let t1;
        if ($[9] !== childProps.className) {
          t1 = extractFileInfo(childProps.className);
          $[9] = childProps.className;
          $[10] = t1;
        } else {
          t1 = $[10];
        }
        const fileInfo = t1;
        language = fileInfo.language;
        filename = fileInfo.filename;
      }
    }
    $[3] = children;
    $[4] = filename;
    $[5] = language;
    $[6] = code;
  } else {
    filename = $[4];
    language = $[5];
    code = $[6];
  }
  if (!code) {
    let t1;
    if ($[11] !== children || $[12] !== props) {
      t1 = <pre className="bg-muted p-4 rounded-lg text-sm font-mono overflow-x-auto mb-4" {...props}>{children}</pre>;
      $[11] = children;
      $[12] = props;
      $[13] = t1;
    } else {
      t1 = $[13];
    }
    return t1;
  }
  const languageLabel = typeof language === "string" && language !== "text" ? language : undefined;
  const t1 = filename ? `Code example: ${filename}` : languageLabel ? `${languageLabel} code example` : "Code example";
  let t2;
  if ($[14] !== filename) {
    t2 = filename && <figcaption className="flex items-center gap-2 border-b border-border bg-muted/50 px-4 py-2"><svg className="h-4 w-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg><span className="text-sm font-medium text-foreground">{filename}</span></figcaption>;
    $[14] = filename;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  let t3;
  if ($[16] !== code) {
    t3 = <CopyButton code={code} />;
    $[16] = code;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== language || $[19] !== props) {
    t4 = t5 => {
      const {
        className,
        style,
        tokens,
        getLineProps,
        getTokenProps
      } = t5;
      return <pre className={`${className} p-4 overflow-x-auto text-sm font-mono leading-relaxed`} style={{
        ...style,
        margin: 0,
        background: "transparent"
      }} {...props}><code className={`${className} block`}>{tokens.map((line, i) => {
            const lineProps = getLineProps({
              line
            });
            const diffColor = getDiffColor(language, line, "light");
            const lineClassName = `${lineProps.className ?? ""} table-row`.trim();
            return <span key={i} {...lineProps} className={lineClassName}><span className="table-cell pr-4 text-muted-foreground select-none text-right opacity-50">{i + 1}</span><span className="table-cell">{line.map((token, key) => {
                  const tokenProps = getTokenProps({
                    token
                  });
                  const isDiffMarkerToken = key === 0 && (token.content.startsWith("+") || token.content.startsWith("-"));
                  if (diffColor && isDiffMarkerToken) {
                    tokenProps.style = {
                      ...tokenProps.style,
                      color: diffColor
                    };
                  }
                  return <span key={key} {...tokenProps} />;
                })}</span></span>;
          })}</code></pre>;
    };
    $[18] = language;
    $[19] = props;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== code || $[22] !== language || $[23] !== t4) {
    t5 = <div className="block dark:hidden"><Highlight code={code} language={language} theme={customLightTheme}>{t4}</Highlight></div>;
    $[21] = code;
    $[22] = language;
    $[23] = t4;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[25] !== language || $[26] !== props) {
    t6 = t7 => {
      const {
        className: className_0,
        style: style_0,
        tokens: tokens_0,
        getLineProps: getLineProps_0,
        getTokenProps: getTokenProps_0
      } = t7;
      return <pre className={`${className_0} p-4 overflow-x-auto text-sm font-mono leading-relaxed`} style={{
        ...style_0,
        margin: 0,
        background: "transparent"
      }} {...props}><code className={`${className_0} block`}>{tokens_0.map((line_0, i_0) => {
            const lineProps_0 = getLineProps_0({
              line: line_0
            });
            const diffColor_0 = getDiffColor(language, line_0, "dark");
            const lineClassName_0 = `${lineProps_0.className ?? ""} table-row`.trim();
            return <span key={i_0} {...lineProps_0} className={lineClassName_0}><span className="table-cell pr-4 text-muted-foreground select-none text-right opacity-50">{i_0 + 1}</span><span className="table-cell">{line_0.map((token_0, key_0) => {
                  const tokenProps_0 = getTokenProps_0({
                    token: token_0
                  });
                  const isDiffMarkerToken_0 = key_0 === 0 && (token_0.content.startsWith("+") || token_0.content.startsWith("-"));
                  if (diffColor_0 && isDiffMarkerToken_0) {
                    tokenProps_0.style = {
                      ...tokenProps_0.style,
                      color: diffColor_0
                    };
                  }
                  return <span key={key_0} {...tokenProps_0} />;
                })}</span></span>;
          })}</code></pre>;
    };
    $[25] = language;
    $[26] = props;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== code || $[29] !== language || $[30] !== t6) {
    t7 = <div className="hidden dark:block"><Highlight code={code} language={language} theme={customDarkTheme}>{t6}</Highlight></div>;
    $[28] = code;
    $[29] = language;
    $[30] = t6;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  let t8;
  if ($[32] !== t1 || $[33] !== t2 || $[34] !== t3 || $[35] !== t5 || $[36] !== t7) {
    t8 = <figure className="group relative mb-6 overflow-hidden rounded-lg border border-border bg-card" aria-label={t1}>{t2}{t3}{t5}{t7}</figure>;
    $[32] = t1;
    $[33] = t2;
    $[34] = t3;
    $[35] = t5;
    $[36] = t7;
    $[37] = t8;
  } else {
    t8 = $[37];
  }
  return t8;
};